<template>
  <div v-show="visibleGrids.length" ref="elementRef" :class="{ 'overflow-x-clip': isEdgeToEdge }">
    <div class="space-y-4 lg:space-y-3" :class="insetClasses">
      <div
        class="f-col gap-x-8 gap-y-3 wrap"
        :class="[
          {
            'o:lg:flex-row o:lg:items-baseline': tabPosition === 'carousel-right' && alignment === 'left',
          },
          titlePadding,
        ]"
      >
        <h2
          v-if="title"
          v-style:c="titleColor"
          :class="[titleStyle, { 'self-center text-center': alignment === 'center' }]"
        >
          <span v-if="$feature.showTitleHtml" v-html="resolvedTitleHtml" />
          <span v-else>{{ resolvedTitle }}</span>
        </h2>
        <vf-carousel-elevate
          v-if="visibleGrids.length > 1"
          :active="tabPosition !== 'wrapped'"
          :bleed="isEdgeToEdge"
          class="w-full"
          :class="{ 'justify-center': alignment === 'center' }"
          :class-container="tabCarouselClasses"
          :show-arrows="false"
          :style-container="tabCarouselStyles"
        >
          <cms-shared-button
            v-for="(gridItem, i) in visibleGrids"
            :key="i"
            :text="withParentCategory(gridItem.title)"
            v-bind="getTabStyle(i)"
            @click="activateGrid(i)"
          />
        </vf-carousel-elevate>
      </div>

      <template
        v-for="(gridItem, i) in visibleGrids"
        :key="i"
      >
        <cms-product-grid-internal
          v-if="mountedGrids.has(i)"
          v-show="i === activeTab"
          v-bind="{
            carouselGaps,
            carouselStyle,
            colsLg,
            ctaStyle,
            edgeToEdge: isEdgeToEdge,
            enableLoop,
            gridItem,
            initialProductsToShow,
            isGrid,
            moreProductsToShow,
            showFocusedProduct,
            title: delocalizedTracking,
            textLinkStyles: getTextLinkStyles(content),
            ssrKey: `product-grid-${route.path}-${id}-${i}`,
            variant: cardVariant,
          }"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ResponsiveStyles } from '#core/directives/style'
import type { Responsive } from '#types/common'
import type { ProductGridContent } from '#types/components/cms/product-grid'
import type { Target } from '#types/target'
import { VfCarouselElevate } from '#components'
import { getStyle } from '#core/directives/style'

const { content, edgeToEdge, pageContext } = defineProps<{
  content: ProductGridContent
  pageContext?: boolean
  edgeToEdge?: boolean
}>()

const {
  ctaStyle,
  enableLoop,
  id,
  itemsToLoad = '1x',
  itemsToShow = 24,
  productGrid,
  delocalizedTracking,
  tabDisplay = 'left-carousel-right',
  tabStyle,
  tabTextLinkColor,
  tabTextLinkHoverColor,
  tabTextLinkStyle,
  tabTextLinkUnderline,
  title,
  titleColor,
  titleHtml,
  titleStyle = 'subtitle-2',
  variant
} = content

const route = useRoute()
const { $feature } = useNuxtApp()
const pdp = usePdp()

const appConfig = useAppConfig()
const componentsAppConfig = appConfig.components
const { gap } = componentsAppConfig.base?.grid
const { variantStyleMap } = componentsAppConfig.cms.bloomreachRecommendations
const { activeButtonMapping, activeClassOverride } = componentsAppConfig.cms.sharedButton
const { grid, productsPerPage } = componentsAppConfig.product.list
const { ctaGap, textLinkGap } = componentsAppConfig.cms.productGrid.brandStyles

const parentCategory = computed(() => pdp?.product.value?.breadcrumbs?.at(-1)?.label || '')
const withParentCategory = (text?: string) => replaceAll(text || '', { 'parent-category': parentCategory.value })
const resolvedTitle = computed(() => withParentCategory(title))
const resolvedTitleHtml = computed(() => withParentCategory(titleHtml))

const { elementRef } = usePromotionTracking(reactive({
  promotionId: id,
  creativeName: resolvedTitle.value,
  promotionName: delocalizedTracking as string
}), content.name)

const titlePadding = pageContext ? getInvertedPaddings(content, componentsAppConfig.cms.section) : ''
// The section skips its inset container for this component type, so the root can span the full
// viewport width (the bleed clip box) and the CMS-configured inset is applied inside it instead
const insetClasses = getContentInset(content, componentsAppConfig.cms.section)

const firstDash = tabDisplay.indexOf('-')
const alignment = tabDisplay.substring(0, firstDash)
const tabPosition = tabDisplay.substring(firstDash + 1)

const { classes: tabCarouselExtraClasses, style: tabCarouselStyles } = getStyle({ gap: Object.keys(appConfig.ds.breakpoints).reduce((acc, bp) => ({
  ...acc,
  [bp]: getValueForBreakpoint(bp as keyof Responsive, tabStyle)?.includes('text-link') ? textLinkGap : ctaGap
}), {} as Responsive) } as ResponsiveStyles)

const tabCarouselClasses = [
  {
    'wrap': tabPosition === 'wrapped',
    'justify-center': tabPosition === 'wrapped' && alignment === 'center'
  },
  tabCarouselExtraClasses
]

const isGrid = !!variant?.startsWith('grid')

// Outside a page context the section strips componentSidePaddings/componentMaxWidth,
// so there is no viewport-centered container for the bleed geometry to align to.
// Grid variants have no carousel to bleed, so they keep the standard inset layout
const isEdgeToEdge = computed(() =>
  pageContext && !isGrid && (edgeToEdge || !!componentsAppConfig.cms.productGrid.edgeToEdge))

const gridMappingExceptions = {
  'grid-1-3': 1,
  'grid-3-3': 4,
}

const split = variant?.split('-')
const rowsLg = Number(split?.[0] === 'grid' ? split[1] : 0)
const colsLg = Number(split?.[0] === 'grid' ? split[2] : 0)
const rowsSm = isGrid ? (gridMappingExceptions[variant!] || (rowsLg * colsLg) / 2) : 0
const cardVariant = `${split?.[0] === 'grid' ? split[2] : split?.[0]}-cards`

const initialProductsToShow = {
  sm: isGrid ? rowsSm * 2 : itemsToShow,
  lg: isGrid ? rowsLg * colsLg : itemsToShow,
}

const pageMultiplier = Number.parseInt(itemsToLoad)

const moreProductsToShow = {
  sm: Math.min(
    Number.isNaN(pageMultiplier) ? productsPerPage : initialProductsToShow.sm * pageMultiplier,
    productsPerPage
  ),
  lg: Math.min(Number.isNaN(pageMultiplier)
    ? productsPerPage
    : initialProductsToShow.lg * pageMultiplier, productsPerPage),
}

const gaps = isObject(grid.gaps.x) ? grid.gaps.x : { sm: grid.gaps.x }
const carouselGaps = filterObject(mapObject(gaps, (v) => v ? `calc(${gap[v]} / 2)` : undefined)) as Responsive
const carouselStyle = !isGrid ? { px: carouselGaps, w: variantStyleMap[variant!] } : undefined

const activeTab = ref(0)
const mountedGrids = ref(new Set([0]))

const showFocusedProduct = computed(() => $feature.showFocusedProductOnRoutes
  .some((pattern) => convertPatternToRegex(pattern).test(route.fullPath)))

const isPdp = computed(() => PRODUCT_URL_REGEX.test(route.path))

const visibleGrids = computed(() => productGrid.filter((gridItem) => {
  const isHidden = gridItem.gridType === 'category' && !gridItem.categoryId && !isPdp.value
  if (isHidden) {
    log.info(`Component ID: ${id} - [ProductGrid] Hiding "${gridItem.title}" tab: category grid without a categoryId outside a PDP`)
  }
  return !isHidden
}))

const getTabStyle = (i: number) => {
  const target: Target = {
    style: tabStyle,
    textLinkColor: tabTextLinkColor,
    textLinkHoverColor: tabTextLinkHoverColor,
    textLinkStyle: tabTextLinkStyle,
    textLinkUnderline: tabTextLinkUnderline,
  }

  if (activeTab.value === i) {
    adjustActiveButtonStyle(target, activeButtonMapping, activeClassOverride)
  }

  return target
}

const activateGrid = (i: number) => {
  activeTab.value = i
  mountedGrids.value.add(i)
}
</script>
